import { ProductType as OfferingProductType } from 'screens/SelectServices/schemas';

export type QuestionnaireType = {
  type: string;
  title: string;
  body: string;
  tags?: string[];
  disabled?: boolean;
};

export type QuestionsType = {
  title: string;
  questions: QuestionnaireType[];
};

export type ProductType = {
  id: string;
  title: string;
  img: string | null;
  manufacturer?: string;
  from_price?: string;
  monthly_price?: string;
  months?: string;
  content?: string;
  stock?: number;
  business?: boolean;
  createdDate?: string;
  fullPriceInfo?: string;
  varients?: VariantsType[];
  productId?: number;
  productDescType?: ProductDescType;
  productDetail1DescType?: ProductDetail1DescType;
  category?: AccessProductGridType['category'];
};

export type ServiceSummaryData = {
  id: string;
  type?: string;
  ip_type: string;
  bandwidth_download: string;
  bandwidth_upload: string;
};

export type TagType =
  | 'Best offer'
  | 'Premium choice'
  | 'Lowest price'
  | 'Other option';

export type OfferingType = {
  id?: string;
  tag: TagType;
  price?: number;
  carrier?: string;
  construction_likely?: boolean;
  details?: {
    id?: string;
    icon: React.ElementType | JSX.Element;
    text: string;
  }[];
};

export type CategorizedOfferings = {
  bestOffer?: OfferingProductType;
  bestOfferDetails?: OfferingType;
  premiumChoice?: OfferingProductType;
  premiumChoiceDetails?: OfferingType;
  lowestPrice?: OfferingProductType;
  lowestPriceDetails?: OfferingType;
  allOtherOptions: OfferingProductType[];
  sortedOfferings?: OfferingProductType[];

  //starlink specific
  otherOption?: OfferingProductType;
  otherOptionDetails?: {
    id: string;
    tag: TagType;
  };
};

export type AccessProductType = {
  id?: string;
  type: string;
  title?: string | JSX.Element;
  content?: string;
  offerings?: OfferingType[] | [];
};
export type CommonAttributesProps = {
  handleStep?: (step: number, config?: { scrollTo?: boolean }) => void;
  step?: number;
};

// Mobility API Response
export interface BaseResponseMobilityAPI<T> {
  data: T;
  hasData: boolean;
  isError: boolean;
  errors: unknown[];
  message: string;
  count?: number;
}

export const ItemType = {
  ACCESS: 'ACCESS',
  MOBILITY: 'MOBILITY',
} as const;

export type ItemType = (typeof ItemType)[keyof typeof ItemType];

export const ItemSubtype = {
  DIA: 'DIA',
  BROADBAND: 'Broadband',
  SMARTPHONE: 'Smartphone',
  TABLET: 'Tablet',
  SIM: 'Sim',
  MODEM: 'Modem',
  ROUTER: 'Router',
  PLAN: 'Plan',
  DATA: 'Data',
  BASIC_CELL: 'Basic',
  ACCESSORY: 'Accessory',
} as const;

export type ItemSubtype = (typeof ItemSubtype)[keyof typeof ItemSubtype];

export type ProductDescType =
  | 'WIRELESS ROUTER'
  | 'SIM CARD'
  | 'TABLET'
  | 'SMART PHONE'
  | 'BASIC CELL PHONE'
  | 'HOTSPOT'
  | 'MODEM'
  | 'UNIVERSAL'
  | 'DATA'
  | 'PLAN'
  | null;

export type ProductDetail1DescType = 'ACCESSORY' | 'DEVICE' | null;

export type MobilityProductTypes = {
  productTypeDesc: 'EQUIPMENT';
  productDetail1Desc: ProductDetail1DescType;
  productDesc: ProductDescType;
  children: [];
};

export type MobilityProduct = {
  productQty: number;
  productDesc: ProductDescType;
  productDetail1Desc: ProductDetail1DescType;
  variants: VariantsType[];
  images: {
    productImageId: number;
    productId: number;
    fileName: string;
    url: string;
    sortOrder: number;
    createdDate: string;
    modifiedDate: string;
  }[];
  compatibleESims: boolean;
  productId: number;
  graniteId: string;
  featured: boolean;
  onSale: boolean;
  bestSeller: boolean;
  description: string;
  createdDate: string;
  modifiedDate: string;
  inPIMData: boolean;
};

export type VariantsType = {
  id: string | null;
  children: VariantsChildrenType[];
  maxPurchasePrice: number;
  minPurchasePrice: number;
  productAlias: string;
  productCode: string;
  productCodeDescription: string;
};

export type VariantsChildrenType = {
  contractTerm_Months: number | null;
  costCode: string;
  id: string;
  licenseIncluded: boolean;
  msrp: null;
  originalPrice: number;
  pimProductId: number;
  pimProductPricingId: number;
  pricingId: number;
  productGroupDesc: 'PURCHASED' | 'FINANCED';
  standardRate: number;
  utilitiesId: string;
};

export type ProductCategory =
  | 'Broadband'
  | 'DIA'
  | 'Granite MNS'
  | 'Starlink'
  | 'All';

export type AccessProductGridType = {
  category: ProductCategory;
  description: string;
  id: number;
  image_path: string;
  image_url: string;
  name: string;
  price_info: string;
};

export enum NetworkDeviceType {
  wirelessRouter = 'WIRELESS ROUTER', // * DEVICE
  modem = 'MODEM', // * DEVICE
}

export enum CellularDeviceType {
  basicPhone = 'BASIC CELL PHONE', // * DEVICE
  smartPhone = 'SMART PHONE', // * DEVICE
  tablet = 'TABLET', // * DEVICE
  hotspot = 'HOTSPOT', // * DEVICE
  sim = 'SIM CARD', // * ACCESSORY
}

export enum DeviceType {
  basicPhone = 'BASIC CELL PHONE', // * DEVICE
  smartPhone = 'SMART PHONE', // * DEVICE
  tablet = 'TABLET', // * DEVICE
  hotspot = 'HOTSPOT', // * DEVICE
  sim = 'SIM CARD', // * ACCESSORY
  modem = 'MODEM', // * DEVICE
  wirelessRouter = 'WIRELESS ROUTER', // * DEVICE
  universal = 'UNIVERSAL', // * ACCESSORY
}

export type PlanTypeConnection =
  | 'INDIVIDUAL CONNECTION'
  | 'POOLED CONNECTION'
  | 'FIXED WIRELESS';

export type CarrierType =
  | 'Granite'
  | 'Granite Multi Carrier SIM'
  | 'VERIZON WIRELESS'
  | 'ATT'
  | 'TMOBILE';

export const DeviceGroupType = {
  DEVICE: [
    DeviceType.basicPhone,
    DeviceType.smartPhone,
    DeviceType.tablet,
    DeviceType.hotspot,
    DeviceType.modem,
    DeviceType.wirelessRouter,
  ],
  ACCESSORY: [
    DeviceType.sim,
    DeviceType.universal,
    DeviceType.wirelessRouter,
    DeviceType.smartPhone,
    DeviceType.tablet,
  ],
};

export type MobilityProductExtraAttributesType = {
  contract_term_months: number;
  description: string;
  pim_product_id: number;
  pim_product_pricing_id: number;
  product_alias: string;
  carriers: ExtraAttributesCarrierType[];
};

export type ExtraAttributesCarrierType = {
  carrier_name: CarrierType;
  sim_types: ExtraAttributesSimType[];
};

export type ExtraAttributesSimType = {
  sim_type: string;
  plan_types: ExtraAttributesPlanType[];
  sim_options: ExtraAttributesOptionType[];
};

export type ExtraAttributesPlanType = {
  connection_type: PlanTypeConnection;
  plan_options: ExtraAttributesOptionType[];
};

export type ExtraAttributesOptionType = {
  compatible_provider: string;
  contract_term_months: number;
  data_plan_mb: number;
  description: string;
  pim_product_id: number;
  pim_product_pricing_id: number;
  product_alias: string;
  standard_rate: number;
};
